// To whichever poor soul has to maintain this code in the future, I apologize for whatever the fuck this file is. Lock up all sharp objects in the vicinity.

// DEPENDENCIES
import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// CSS
import "./PendingJobReviewScreen.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	getAPICall,
	putAPICall,
	postAPICall,
} from "../../../../config/apiCalls";
import { loginRequest } from "../../../../config/authConfig";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Button,
	CircularProgress,
	Collapse,
	IconButton,
	LinearProgress,
} from "@mui/material";

import { Close as CloseIcon } from "@mui/icons-material";

// CUSTOM COMPONENTS
import GeneralInformationCard from "../../../Proposals/PendingJobs/GeneralInformationCard/GeneralInformationCard";
import DropboxInformationCard from "../../../Proposals/PendingJobs/DropboxInformationCard/DropboxInformationCard";
import BQEInformationCard from "../../../Proposals/PendingJobs/BQEInformationCard/BQEInformationCard";
import CompanyCamInformationCard from "../../../Proposals/PendingJobs/CompanyCamInformationCard/CompanyCamInformationCard";

// CONSTANTS
import { getCurrentTimeUTC, getYear } from "../../../../constants/dateTime";
import { generateAlphanumericCode } from "../../../../constants/constants";
import { zipCodeRegex } from "../../../../constants/regex";

// REACT COMPONENT
const PendingJobReviewScreen = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// REFS
	const successMessageRef = useRef(null);
	const topScreenRef = useRef(null);

	// PARAMS
	const { id } = useParams();

	// STATES
	const [pendingJobRecord, setPendingJobRecord] = useState({});
	const [allPhases, setAllPhases] = useState([]);
	const [allComponents, setAllComponents] = useState([]);
	const [allBuildingTypes, setAllBuildingTypes] = useState([]);
	const [allContractTypes, setAllContractTypes] = useState([]);
	const [activeEmployees, setActiveEmployees] = useState([]);
	const [selectedComponents, setSelectedComponents] = useState([]);

	const [propertyInfo, setPropertyInfo] = useState({
		name: "",
		addressLine1: "",
		addressLine2: "",
		city: "",
		state: "",
		zip: "",
		buildingTypeID: null,
	});

	const [projectInfo, setProjectInfo] = useState({
		name: "",
		displayName: "",
		oic: "",
	});

	const [phaseBudgets, setPhaseBudgets] = useState({
		phase1: "0",
		phase2: "0",
		phase3: "0",
		phase4: "0",
	});

	const [phaseCostBases, setPhaseCostBases] = useState({
		phase1: "",
		phase2: "",
		phase3: "",
		phase4: "",
	});

	const [numberOfPhases, setNumberOfPhases] = useState(1);

	const [phase1, setPhase1] = useState({});
	const [phase2, setPhase2] = useState({});
	const [phase3, setPhase3] = useState({});
	const [phase4, setPhase4] = useState({});

	const [dealOwner, setDealOwner] = useState({});

	const [filePath, setFilePath] = useState("/Projects");
	const [folderCreationLevel, setFolderCreationLevel] = useState("");

	const [bqePropertyRecord, setBqePropertyRecord] = useState(null);
	const [bqeProjectRecordID, setBqeProjectRecordID] = useState(null);

	const [bqePropertyRecordCreateNew, setBqePropertyRecordCreateNew] =
		useState(false);
	const [bqeProjectRecordCreateNew, setBqeProjectRecordCreateNew] =
		useState(false);

	// Feedback
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const [errorAlertOpen, setErrorAlertOpen] = useState(false);
	const [errorAlertMessage, setErrorAlertMessage] = useState("");

	const [successAlertOpen, setSuccessAlertOpen] = useState(false);
	const [successAlertMessage, setSuccessAlertMessage] = useState("");

	const [progressBarOpen, setProgressBarOpen] = useState(false);
	const [progressBarLevel, setProgressBarLevel] = useState(0);
	const [progressBarMessage, setProgressBarMessage] = useState("");

	// Validation
	const [isDropboxInformationValid, setIsDropboxInformationValid] =
		useState(false);
	const [isBqeInformationValid, setIsBqeInformationValid] = useState(false);

	const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	const summarizeOutput = (
		value,
		seen = new WeakSet(),
		depth = 0,
		maxDepth = 4
	) => {
		if (value === undefined || value === null) return value ?? null;
		if (depth > maxDepth) return "[MaxDepthExceeded]";
		if (typeof value !== "object") return value;
		if (seen.has(value)) return "[Circular]";
		seen.add(value);

		if (Array.isArray(value)) {
			return value.map((v) =>
				summarizeOutput(v, seen, depth + 1, maxDepth)
			);
		}

		if ("status" in value || "data" in value || "error" in value) {
			return {
				status: value?.status ?? null,
				data: summarizeOutput(value?.data ?? null, seen, depth + 1),
				error:
					value?.error?.message ||
					value?.error ||
					value?.data?.error ||
					null,
			};
		}

		const redactedKeys = new Set([
			"authorization",
			"cookie",
			"set-cookie",
			"access_token",
			"refresh_token",
			"token",
		]);

		const summarized = {};
		Object.keys(value).forEach((key) => {
			if (redactedKeys.has(key.toLowerCase())) {
				summarized[key] = "[Redacted]";
				return;
			}
			summarized[key] = summarizeOutput(
				value[key],
				seen,
				depth + 1,
				maxDepth
			);
		});
		return summarized;
	};

	const getSubmittedByName = () => {
		const employeeRecord = activeEmployees.find((e) => e.id === employeeID);
		if (!employeeRecord) return null;
		return `${employeeRecord.first_name} ${employeeRecord.last_name}`;
	};

	const submitPendingJobFormLog = async ({
		actionType,
		actionStatus,
		errorMessage = null,
		actionClickedTime = null,
		actionCompletedTime = null,
		outcome = {},
		stepOutputs = {},
	} = {}) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/pendingJobs", {
				actionType,
				actionStatus,
				errorMessage,
				pendingJobID: pendingJobRecord.id ?? null,
				submittedByEmployeeID: employeeID ?? null,
				submittedByEmployeeName: getSubmittedByName(),
				actionClickedTime,
				actionCompletedTime,
				propertyName: propertyInfo.name?.trim() ?? null,
				buildingTypeID: propertyInfo.buildingTypeID ?? null,
				propertyAddressLine1: propertyInfo.addressLine1?.trim() ?? null,
				propertyAddressLine2: propertyInfo.addressLine2?.trim() ?? null,
				propertyCity: propertyInfo.city?.trim() ?? null,
				propertyState: propertyInfo.state ?? null,
				propertyZip: propertyInfo.zip ?? null,
				projectName: projectInfo.name ?? null,
				oic: projectInfo.oic ?? null,
				phase1ID: phase1.id ?? null,
				phase2ID: phase2.id ?? null,
				phase3ID: phase3.id ?? null,
				phase4ID: phase4.id ?? null,
				phase1Budget: phaseBudgets.phase1 ?? null,
				phase2Budget: phaseBudgets.phase2 ?? null,
				phase3Budget: phaseBudgets.phase3 ?? null,
				phase4Budget: phaseBudgets.phase4 ?? null,
				phase1CostBasis: phaseCostBases.phase1 ?? null,
				phase2CostBasis: phaseCostBases.phase2 ?? null,
				phase3CostBasis: phaseCostBases.phase3 ?? null,
				phase4CostBasis: phaseCostBases.phase4 ?? null,
				dealOwnerID: dealOwner.id ?? null,
				dropboxFilePath: filePath ?? null,
				folderCreationLevel: folderCreationLevel ?? null,
				bqePropertyID: bqePropertyRecord?.id ?? null,
				bqeProjectID: bqeProjectRecordID ?? null,
				bqePPCreateNew: bqePropertyRecordCreateNew ?? null,
				bqePRCreateNew: bqeProjectRecordCreateNew ?? null,
				hsDealID: pendingJobRecord.hs_deal_id ?? null,
				hsQuoteID: pendingJobRecord.hs_quote_id ?? null,
				hsCompanyID: pendingJobRecord.hs_company_id ?? null,
				hsCompanyName: pendingJobRecord.hs_company_name ?? null,
				hsCompanyAddressLine1:
					pendingJobRecord.hs_company_address_line1 ?? null,
				hsCompanyAddressLine2:
					pendingJobRecord.hs_company_address_line2 ?? null,
				hsCompanyCity: pendingJobRecord.hs_company_city ?? null,
				hsCompanyState: pendingJobRecord.hs_company_state ?? null,
				hsCompanyZip: pendingJobRecord.hs_company_zip ?? null,
				bqeStateClassCode: pendingJobRecord.bqe_state_class_code ?? null,
				contactName: pendingJobRecord.contact_name ?? null,
				contactEmail: pendingJobRecord.contact_email ?? null,
				createdPropertyID: outcome.propertyID ?? null,
				createdProjectID: outcome.projectID ?? null,
				createdPhase1ID: outcome.phase1ID ?? null,
				createdPhase2ID: outcome.phase2ID ?? null,
				createdPhase3ID: outcome.phase3ID ?? null,
				createdPhase4ID: outcome.phase4ID ?? null,
				dealOwnerNotified: outcome.dealOwnerNotified ?? null,
				projectCoordinatorNotified:
					outcome.projectCoordinatorNotified ?? null,
				payloadSnapshot: {
					pendingJobRecord,
					propertyInfo,
					projectInfo,
					phaseBudgets,
					phaseCostBases,
					dealOwner,
					filePath,
					folderCreationLevel,
					bqePropertyRecord,
					bqeProjectRecordID,
					bqePropertyRecordCreateNew,
					bqeProjectRecordCreateNew,
					outcome,
					stepOutputs: summarizeOutput(stepOutputs),
				},
				modifiedBy: employeeID ?? null,
			});
		} catch (error) {
			// Avoid noisy console logs in production; failure to log should not block UX.
		}
	};

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Pending Job Review",
			employeeID: employeeID,
			notes: "Accessed the Pending Job Review page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchPendingJobRecord();
	}, []);

	// This useEffect is used to determine if there are any unsaved changes
	useEffect(() => {
		if (!pendingJobRecord.id) return;

		if (
			propertyInfo.name != pendingJobRecord.property_name ||
			propertyInfo.addressLine1 !=
				pendingJobRecord.property_address_line1 ||
			propertyInfo.addressLine2 !=
				pendingJobRecord.property_address_line2 ||
			propertyInfo.city != pendingJobRecord.property_city ||
			propertyInfo.state != pendingJobRecord.property_state ||
			propertyInfo.zip != pendingJobRecord.property_zip ||
			projectInfo.oic != pendingJobRecord.oic ||
			projectInfo.name != pendingJobRecord.project_name ||
			phase1.id != pendingJobRecord.phase1_id ||
			phase2.id != pendingJobRecord.phase2_id ||
			phase3.id != pendingJobRecord.phase3_id ||
			phase4.id != pendingJobRecord.phase4_id ||
			phaseBudgets.phase1 != pendingJobRecord.phase1_budget ||
			phaseBudgets.phase2 != pendingJobRecord.phase2_budget ||
			phaseBudgets.phase3 != pendingJobRecord.phase3_budget ||
			phaseBudgets.phase4 != pendingJobRecord.phase4_budget ||
			phaseCostBases.phase1 != pendingJobRecord.phase1_cost_basis ||
			phaseCostBases.phase2 != pendingJobRecord.phase2_cost_basis ||
			phaseCostBases.phase3 != pendingJobRecord.phase3_cost_basis ||
			phaseCostBases.phase4 != pendingJobRecord.phase4_cost_basis ||
			dealOwner.id != pendingJobRecord.deal_owner_id ||
			filePath != pendingJobRecord.dropbox_file_path ||
			(bqePropertyRecord &&
				bqePropertyRecord.id != pendingJobRecord.bqe_property_id) ||
			bqeProjectRecordID != pendingJobRecord.bqe_project_id
		) {
			setIsUnsavedChanges(true);
		} else {
			setIsUnsavedChanges(false);
		}
	}, [
		propertyInfo,
		projectInfo,
		phase1,
		phase2,
		phase3,
		phase4,
		phaseBudgets,
		phaseCostBases,
		dealOwner,
		filePath,
		bqePropertyRecord,
		bqeProjectRecordID,
	]);

	// INPUT HANDLERS
	// Buttons
	const saveButtonPressed = async () => {
		const actionClickedTime = getCurrentTimeUTC();
		try {
			const res = await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/pending/save",
				{
					propertyName: propertyInfo.name.trim(),
					propertyAddressLine1: propertyInfo.addressLine1.trim(),
					propertyAddressLine2: propertyInfo.addressLine2.trim(),
					propertyCity: propertyInfo.city.trim(),
					propertyState: propertyInfo.state,
					propertyZip: propertyInfo.zip,
					buildingTypeID: propertyInfo.buildingTypeID ?? null,
					oic: projectInfo.oic,
					phase1ID: phase1.id ?? null,
					phase2ID: phase2.id ?? null,
					phase3ID: phase3.id ?? null,
					phase4ID: phase4.id ?? null,
					phase1Budget: phaseBudgets.phase1,
					phase2Budget: phaseBudgets.phase2,
					phase3Budget: phaseBudgets.phase3,
					phase4Budget: phaseBudgets.phase4,
					phase1CostBasis: phaseCostBases.phase1 ?? null,
					phase2CostBasis: phaseCostBases.phase2 ?? null,
					phase3CostBasis: phaseCostBases.phase3 ?? null,
					phase4CostBasis: phaseCostBases.phase4 ?? null,
					dealOwnerID: dealOwner.id ?? null,
					dropboxFilePath: filePath ?? null,
					projectName: projectInfo.name ?? null,
					bqePropertyID:
						bqePropertyRecord && bqePropertyRecord.id
							? bqePropertyRecord.id
							: null,
					bqeProjectID: bqeProjectRecordID ?? null,
					bqePPCreateNew: bqePropertyRecordCreateNew ?? false,
					bqePRCreateNew: bqeProjectRecordCreateNew ?? false,
					id: pendingJobRecord.id,
				},
				employeeID
			);

			if (res.data && res.data.error) {
				await submitPendingJobFormLog({
					actionType: "save",
					actionStatus: "failure",
					errorMessage: res.data.error,
					actionClickedTime,
					actionCompletedTime: getCurrentTimeUTC(),
					stepOutputs: {
						saveResponse: summarizeOutput(res),
					},
				});
				openErrorAlert(res.data.error);
				return;
			}

			await fetchPendingJobRecord();
			await submitPendingJobFormLog({
				actionType: "save",
				actionStatus: "success",
				actionClickedTime,
				actionCompletedTime: getCurrentTimeUTC(),
				stepOutputs: {
					saveResponse: summarizeOutput(res),
				},
			});

			openSuccessAlert("Pending Job Updated Successfully");
			return;
		} catch (err) {
			await submitPendingJobFormLog({
				actionType: "save",
				actionStatus: "failure",
				errorMessage: err?.message || "Error saving pending job.",
				actionClickedTime,
				actionCompletedTime: getCurrentTimeUTC(),
				stepOutputs: {
					saveError: summarizeOutput(err),
				},
			});
			console.log(err);
		}
	};

	const submitButtonPressed = async () => {
		const actionClickedTime = getCurrentTimeUTC();
		// TODO: top of function
		setIsSubmitted(true);
		closeSuccessAlert();
		closeErrorAlert();
		const stepOutputs = {};

		const handleSubmitFailure = async (errorMessage, alertMessage = null) => {
			await submitPendingJobFormLog({
				actionType: "submit",
				actionStatus: "failure",
				errorMessage,
				actionClickedTime,
				actionCompletedTime: getCurrentTimeUTC(),
				stepOutputs,
			});
			if (alertMessage) {
				openErrorAlert(alertMessage);
			}
			setIsSubmitted(false);
		};

		try {
			// Validate all information
			const generalValid = validateGeneralInformation();
			const dropboxValid = validateDropboxInformation();
			const bqeValid = validateBQEInformation();

			if (!generalValid || !dropboxValid || !bqeValid) {
				await handleSubmitFailure("Validation failed.");
				return;
			}

			setProgressBarOpen(true);

			// Create all dropbox folders if necessary
			editProgressBar(0, "Creating Dropbox Folders");
			const dropboxCreated = await createDropboxFolders();
			stepOutputs.dropboxCreated = summarizeOutput(dropboxCreated);
			if (!dropboxCreated) {
				await handleSubmitFailure("Failed to create Dropbox folders.");
				return;
			}

			// Generate phase/project codes and create BQE records
			editProgressBar(40, "Creating BQE Records");
			const propertyCode = await generatePropertyCode();
			const projectCode = await generateProjectCode();
			const phase1Code = await generatePhaseCode();
			const phase2Code = await generatePhaseCode();
			const phase3Code = await generatePhaseCode();
			const phase4Code = await generatePhaseCode();

			const bqeCreated = await createBQERecords(
				propertyCode,
				projectCode,
				phase1Code,
				phase2Code,
				phase3Code,
				phase4Code
			);
			stepOutputs.bqeCreated = summarizeOutput(bqeCreated);

			if (!bqeCreated) {
				await handleSubmitFailure("Failed to create BQE records.");
				return;
			}

			// Create Company Cam records
			editProgressBar(80, "Creating Company Cam Records");
			const ccCreated = await createCompanyCamProjects();
			stepOutputs.companyCamCreated = summarizeOutput(ccCreated);
			if (!ccCreated || ccCreated?.error) {
				const companyCamErrorMessage =
					ccCreated?.error?.detail?.message ||
					ccCreated?.error?.detail ||
					"Failed to create CompanyCam records.";
				await handleSubmitFailure(companyCamErrorMessage);
				return;
			}

			// Create property record in database
			editProgressBar(90, "Creating property record in database");
			const propertyDBID = await createPropertyRecord(
				bqeCreated.propertyID,
				propertyCode,
				dropboxCreated.propertyPath
			);
			stepOutputs.propertyDBID = summarizeOutput(propertyDBID);

			if (!propertyDBID) {
				await handleSubmitFailure(
					"Failed to create property record in database.",
					"Failed to create property record in database."
				);
				return;
			}

			// Create project record in database
			editProgressBar(92, "Creating project record in database");
			const projectDBID = await createProjectRecord(
				bqeCreated.projectID,
				projectCode,
				dropboxCreated.projectPath,
				propertyDBID
			);
			stepOutputs.projectDBID = summarizeOutput(projectDBID);

			if (!projectDBID) {
				await handleSubmitFailure(
					"Failed to create project record in database.",
					"Failed to create project record in database."
				);
				return;
			}

			editProgressBar(94, "Creating phase records in database");
			// Create phase records in database
			const phase1DBID = await createPhaseRecord(
				phase1,
				bqeCreated.phase1ID,
				phase1Code,
				dropboxCreated.phase1Path,
				ccCreated.phase1ID,
				propertyDBID,
				projectDBID
			);
			const phase2DBID = await createPhaseRecord(
				phase2,
				bqeCreated.phase2ID,
				phase2Code,
				dropboxCreated.phase2Path,
				ccCreated.phase2ID,
				propertyDBID,
				projectDBID
			);
			const phase3DBID = await createPhaseRecord(
				phase3,
				bqeCreated.phase3ID,
				phase3Code,
				dropboxCreated.phase3Path,
				ccCreated.phase3ID,
				propertyDBID,
				projectDBID
			);
			const phase4DBID = await createPhaseRecord(
				phase4,
				bqeCreated.phase4ID,
				phase4Code,
				dropboxCreated.phase4Path,
				ccCreated.phase4ID,
				propertyDBID,
				projectDBID
			);
			stepOutputs.phaseDBIDs = summarizeOutput({
				phase1DBID,
				phase2DBID,
				phase3DBID,
				phase4DBID,
			});

			if (
				(phase1?.id && !phase1DBID) ||
				(phase2?.id && !phase2DBID) ||
				(phase3?.id && !phase3DBID) ||
				(phase4?.id && !phase4DBID)
			) {
				await handleSubmitFailure(
					"Failed to create one or more phase records in database.",
					"Failed to create one or more phase records in database."
				);
				return;
			}

			// Send a webhook to notify deal owner
			editProgressBar(95, "Notifying deal owner");
			const dealOwnerRes = await notifyDealOwner(
				propertyDBID,
				projectDBID,
				phase1DBID,
				phase2DBID,
				phase3DBID,
				phase4DBID
			);
			stepOutputs.dealOwnerNotification = summarizeOutput(dealOwnerRes);
			const dealOwnerNotified =
				Boolean(dealOwnerRes) && !dealOwnerRes?.data?.error;
			if (!dealOwnerNotified) {
				await handleSubmitFailure(
					"Failed to notify deal owner.",
					"Failed to notify deal owner."
				);
				return;
			}

			// Update pending job record
			editProgressBar(96, "Updating job record");
			const submittedUpdateRes = await pendingJobSubmitted();
			stepOutputs.pendingJobSubmitted = summarizeOutput(submittedUpdateRes);
			if (!submittedUpdateRes || submittedUpdateRes?.data?.error) {
				await handleSubmitFailure(
					submittedUpdateRes?.data?.error ||
						"Failed to update pending job as submitted.",
					"Failed to update pending job record."
				);
				return;
			}

			// Send a webhook to notify project coordinator
			editProgressBar(99, "Notifying project coordinator");
			const projectCoordinatorRes = await notifyProjectCoordinator(
				propertyDBID,
				projectDBID,
				phase1DBID,
				phase2DBID,
				phase3DBID,
				phase4DBID
			);
			stepOutputs.projectCoordinatorNotification =
				summarizeOutput(projectCoordinatorRes);
			const projectCoordinatorNotified =
				Boolean(projectCoordinatorRes) &&
				!projectCoordinatorRes?.data?.error;
			if (!projectCoordinatorNotified) {
				await handleSubmitFailure(
					"Failed to notify project coordinator.",
					"Failed to notify project coordinator."
				);
				return;
			}

			await submitPendingJobFormLog({
				actionType: "submit",
				actionStatus: "success",
				actionClickedTime,
				actionCompletedTime: getCurrentTimeUTC(),
				stepOutputs,
				outcome: {
					propertyID: propertyDBID,
					projectID: projectDBID,
					phase1ID: phase1DBID,
					phase2ID: phase2DBID,
					phase3ID: phase3DBID,
					phase4ID: phase4DBID,
					dealOwnerNotified,
					projectCoordinatorNotified,
				},
			});

			openSuccessAlert("Pending Job Submitted Successfully");
		} catch (err) {
			stepOutputs.unexpectedError = summarizeOutput(err);
			await handleSubmitFailure(
				err?.message || "Unexpected error while submitting pending job.",
				"Unexpected error while submitting pending job."
			);
			console.log(err);
		}
	};

	// API FUNCTIONS
	const fetchPendingJobRecord = async () => {
		setIsLoading(true);

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/pending/byID",
				{
					id: id,
				}
			);

			setPendingJobRecord(res.data[0]);

			setPropertyInfo({
				name: res.data[0].property_name ?? "",
				addressLine1: res.data[0].property_address_line1 ?? "",
				addressLine2: res.data[0].property_address_line2 ?? "",
				city: res.data[0].property_city ?? "",
				state: res.data[0].property_state ?? "",
				zip: res.data[0].property_zip ?? "",
				buildingTypeID: res.data[0].building_type_id ?? null,
			});

			setProjectInfo((prevInfo) => ({
				...prevInfo,
				oic: res.data[0].oic ?? "",
			}));

			setFilePath(res.data[0].dropbox_file_path);

			if (res.data[0].bqe_property_id)
				await fetchBQEPropertyRecord(res.data[0].bqe_property_id);
			if (res.data[0].bqe_property_id && res.data[0].bqe_project_id)
				setBqeProjectRecordID(res.data[0].bqe_project_id);

			setBqePropertyRecordCreateNew(res.data[0].bqe_pp_create_new);
			setBqeProjectRecordCreateNew(res.data[0].bqe_pr_create_new);

			await fetchBuildingTypes();
			await fetchContractTypes();

			await fetchActiveEmployees();
			await fetchEmployeeRecord(res.data[0].deal_owner_id);
			await fetchPhaseRecords(
				res.data[0].phase1_id,
				res.data[0].phase2_id,
				res.data[0].phase3_id,
				res.data[0].phase4_id
			);
			await fetchComponentIDs(res.data[0].project_name);

			const normalizeCostBasis = (value) => {
				if (!value) return "";

				if (value === "Time and Cost" || value === "Time & Cost") {
					return "timeAndCost";
				}

				if (value === "Flat Fee") {
					return "flatFee";
				}

				return value;
			};

			setPhaseCostBases({
				phase1: normalizeCostBasis(res.data[0].phase1_cost_basis),
				phase2: normalizeCostBasis(res.data[0].phase2_cost_basis),
				phase3: normalizeCostBasis(res.data[0].phase3_cost_basis),
				phase4: normalizeCostBasis(res.data[0].phase4_cost_basis),
			});

			setPhaseBudgets({
				phase1: res.data[0].phase1_budget ?? "0",
				phase2: res.data[0].phase2_budget ?? "0",
				phase3: res.data[0].phase3_budget ?? "0",
				phase4: res.data[0].phase4_budget ?? "0",
			});
		} catch (err) {
			console.log(err);
		}

		setIsLoading(false);
	};

	const fetchEmployeeRecord = async (id) => {
		if (!id) return;

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/byID",
				{
					id: id,
				}
			);

			setDealOwner(res.data[0]);

			return res.data[0];
		} catch (err) {
			console.log(err);
		}
	};

	const fetchActiveEmployees = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/all/validCompanyCam"
			);
			setActiveEmployees(res.data);

			return;
		} catch (err) {
			console.log(err);
		}
	};

	const fetchPhaseRecords = async (p1ID, p2ID, p3ID, p4ID) => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phases/names/all"
			);
			setAllPhases(res.data);

			if (p1ID) {
				const phase1 = res.data.find((phase) => phase.id === p1ID);
				setPhase1(phase1);
				setNumberOfPhases(1);
			}

			if (p2ID) {
				const phase2 = res.data.find((phase) => phase.id === p2ID);
				setPhase2(phase2);
				setNumberOfPhases(2);
			}

			if (p3ID) {
				const phase3 = res.data.find((phase) => phase.id === p3ID);
				setPhase3(phase3);
				setNumberOfPhases(3);
			}

			if (p4ID) {
				const phase4 = res.data.find((phase) => phase.id === p4ID);
				setPhase4(phase4);
				setNumberOfPhases(4);
			}

			return;
		} catch (err) {
			console.log(err);
		}
	};

	const fetchComponentIDs = async (projectName) => {
		try {
			// fetch all components
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/components/all"
			);
			setAllComponents(res.data);

			// split project name into individual components
			const projectNames = projectName.split(", ");

			// find the component records for the project names
			var component1Record = null;
			var component2Record = null;

			var componentList = [];
			var abbreviatedComponentList = [];
			var selectedComponentsList = [];

			if (projectNames.length === 1) {
				component1Record = res.data.find(
					(component) => component.name === projectNames[0]
				);

				if (component1Record && component1Record.name) {
					componentList.push(component1Record.name);
					abbreviatedComponentList.push(
						component1Record.abbreviated_name
					);
					selectedComponentsList.push(component1Record);
				}
			}

			if (projectNames.length === 2) {
				component1Record = res.data.find(
					(component) => component.name === projectNames[0]
				);
				component2Record = res.data.find(
					(component) => component.name === projectNames[1]
				);

				if (component1Record && component1Record.name) {
					componentList.push(component1Record.name);
					abbreviatedComponentList.push(
						component1Record.abbreviated_name
					);
					selectedComponentsList.push(component1Record);
				}

				if (component2Record && component2Record.name) {
					componentList.push(component2Record.name);
					abbreviatedComponentList.push(
						component2Record.abbreviated_name
					);
					selectedComponentsList.push(component2Record);
				}
			}

			// set the component names
			setProjectInfo((prevInfo) => ({
				...prevInfo,
				name: componentList.join(", "),
				displayName: abbreviatedComponentList.join(", "),
			}));

			setSelectedComponents(selectedComponentsList);

			return;
		} catch (err) {
			console.log(err);
		}
	};

	const fetchBQEPropertyRecord = async (propertyID) => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byID",
				{
					id: propertyID,
				}
			);

			setBqePropertyRecord(res.data);

			return;
		} catch (err) {
			console.log(err);
		}
	};

	const fetchBuildingTypes = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/buildingTypes/all"
			);

			setAllBuildingTypes(res.data);

			return res.data;
		} catch (err) {
			console.log(err);
		}
	};

	const fetchContractTypes = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/contractTypes/all"
			);

			setAllContractTypes(res.data);

			return res.data;
		} catch (err) {
			console.log(err);
		}
	};

	// Dropbox
	const createDropboxFolders = async () => {
		try {
			// each project folder will have the year appended to the back of the name
			let year = getYear();

			switch (folderCreationLevel) {
				case "property":
					// PROPERTY LEVEL
					const propertyRes = await createPropertyFolder(
						`${filePath}/${propertyInfo.name}`
					);

					if (propertyRes.data && propertyRes.data.error) {
						if (propertyRes.data.errorResponse === "Conflict") {
							openErrorAlert(
								"Property folder already exists in Dropbox."
							);
							return false;
						}

						openErrorAlert(
							"Failed to create property folder in Dropbox."
						);
						return false;
					}

					// PROJECT LEVEL
					var projectPath = `${propertyRes.data.metadata.path_display}/${projectInfo.displayName} (${year})`;

					var projectRes = await createProjectFolder(projectPath);

					if (projectRes.data && projectRes.data.error) {
						if (projectRes.data.errorResponse === "Conflict") {
							openErrorAlert(
								"Project folder already exists in Dropbox."
							);
							return false;
						}

						openErrorAlert(
							"Failed to create project folder in Dropbox."
						);
						return false;
					}

					// PHASE LEVEL
					var phaseLevelRes = await createPhaseLevelFolders(
						projectPath
					);
					if (!phaseLevelRes) {
						return false;
					}

					var phaseRes = await createPhaseFolders(projectPath);

					if (phaseRes.data && phaseRes.data.error) {
						openErrorAlert(
							"Failed to create phase folders in Dropbox."
						);
						return false;
					}

					return {
						propertyPath: propertyRes.data.metadata.path_display,
						projectPath: projectRes.data.metadata.path_display,
						...phaseLevelRes,
					};

					break;
				case "project":
					var projectPath = filePath;

					// PROJECT LEVEL
					var projectRes = await createProjectFolder(
						`${projectPath}/${projectInfo.displayName} (${year})`
					);

					if (projectRes.data && projectRes.data.error) {
						if (projectRes.data.errorResponse === "Conflict") {
							openErrorAlert(
								"Project folder already exists in Dropbox."
							);
							return false;
						}

						openErrorAlert(
							"Failed to create project folder in Dropbox."
						);
						return false;
					}

					// PHASE LEVEL
					var phaseLevelRes = await createPhaseLevelFolders(
						`${projectPath}/${projectInfo.displayName} (${year})`
					);
					if (!phaseLevelRes) {
						return false;
					}

					var phaseRes = await createPhaseFolders(
						`${projectPath}/${projectInfo.displayName} (${year})`
					);

					if (phaseRes.data && phaseRes.data.error) {
						openErrorAlert(
							"Failed to create phase folders in Dropbox."
						);
						return false;
					}

					return {
						propertyPath: projectPath,
						projectPath: projectRes.data.metadata.path_display,
						...phaseLevelRes,
					};
				case "phase":
					var projectPath = filePath;

					// PHASE LEVEL
					var phaseLevelRes = await createPhaseLevelFolders(
						projectPath
					);
					if (!phaseLevelRes) {
						return false;
					}

					var phaseRes = await createPhaseFolders(`${projectPath}`);

					if (phaseRes.data && phaseRes.data.error) {
						openErrorAlert(
							"Failed to create phase folders in Dropbox."
						);
						return false;
					}

					return {
						propertyPath: projectPath
							.split("/")
							.slice(0, -1)
							.join("/"),
						projectPath: projectPath,
						...phaseLevelRes,
					};
				default:
					openErrorAlert("Could not determine project file path.");
					return false;
			}
		} catch (err) {
			console.log(err);
		}
	};

	const createPropertyFolder = async (path) => {
		editProgressBar(10, "Creating Property Folder");

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/dropbox/create/folder",
				{
					path: path,
				}
			);

			return res;
		} catch (err) {
			console.log(err);
		}
	};

	const createProjectFolder = async (path) => {
		editProgressBar(20, "Creating Project Folder");

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/dropbox/create/folder",
				{
					path: path,
				}
			);

			return res;
		} catch (err) {
			console.log(err);
		}
	};

	const createPhaseFolders = async (projectPath) => {
		// This uses a "batch create" API call to create all the phase folders at once
		try {
			editProgressBar(25, "Fetching Phase Folder Names");
			const generalNames = await fetchGeneralFolderNames(projectPath);
			const phase1Names = await fetchPhase1FolderNames(projectPath);
			const phase2Names = await fetchPhase2FolderNames(projectPath);
			const phase3Names = await fetchPhase3FolderNames(projectPath);
			const phase4Names = await fetchPhase4FolderNames(projectPath);

			const phaseFolderPaths = [
				...generalNames,
				...phase1Names,
				...phase2Names,
				...phase3Names,
				...phase4Names,
			];

			editProgressBar(30, "Creating Phase Folders");

			const phaseFolderPathsWithVideos =
				withVideosUnderPhotos(phaseFolderPaths);

			const subfolderLevelRes = await postAPICall(
				instance,
				accounts[0],
				"/api/dropbox/create/folder/batch",
				{
					paths: phaseFolderPathsWithVideos,
				}
			);

			return subfolderLevelRes;
		} catch (err) {
			console.log(err);
		}
	};

	const createPhaseLevelFolders = async (projectPath) => {
		// These are the folders at the "phase level", (i.e. 1. General, 2. Phase 1, 3. Phase 2, etc.)
		editProgressBar(22, "Creating Phase level Folders");
		var phaseFolderNames = [];

		phaseFolderNames.push(`${projectPath}/1. General`);
		if (phase1 && phase1.id)
			phaseFolderNames.push(
				`${projectPath}/${phase1.steve_number}. ${phase1.abbreviated_name}`
			);
		if (phase2 && phase2.id)
			phaseFolderNames.push(
				`${projectPath}/${phase2.steve_number}. ${phase2.abbreviated_name}`
			);
		if (phase3 && phase3.id)
			phaseFolderNames.push(
				`${projectPath}/${phase3.steve_number}. ${phase3.abbreviated_name}`
			);
		if (phase4 && phase4.id)
			phaseFolderNames.push(
				`${projectPath}/${phase4.steve_number}. ${phase4.abbreviated_name}`
			);

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/dropbox/create/folder/batch",
				{
					paths: phaseFolderNames,
				}
			);

			if (res.data && res.data.error) {
				openErrorAlert(
					"Failed to create phase level folders in Dropbox."
				);
				return false;
			}

			return {
				phase1Path:
					phase1 && phase1.id
						? `${projectPath}/${phase1.steve_number}. ${phase1.abbreviated_name}`
						: null,
				phase2Path:
					phase2 && phase2.id
						? `${projectPath}/${phase2.steve_number}. ${phase2.abbreviated_name}`
						: null,
				phase3Path:
					phase3 && phase3.id
						? `${projectPath}/${phase3.steve_number}. ${phase3.abbreviated_name}`
						: null,
				phase4Path:
					phase4 && phase4.id
						? `${projectPath}/${phase4.steve_number}. ${phase4.abbreviated_name}`
						: null,
			};
		} catch (err) {
			console.log(err);
		}
	};

	const fetchGeneralFolderNames = async (projectPath) => {
		return [
			`${projectPath}/1. General/1.0 Photos`,
			`${projectPath}/1. General/1.1 Admin`,
			`${projectPath}/1. General/1.2 Docs In`,
			`${projectPath}/1. General/1.3 Engineering`,
		];
	};

	const fetchPhase1FolderNames = async (projectPath) => {
		if (!phase1 || !phase1.phase_type_id) return [];

		try {
			const folderListRes = await getAPICall(
				instance,
				accounts[0],
				"/api/dropbox/folders/byPhaseType",
				{
					phaseTypeID: phase1.phase_type_id,
				}
			);

			return folderListRes.data.map(
				(folder) =>
					`${projectPath}/${phase1.steve_number}. ${phase1.abbreviated_name}/${folder.folder_name}`
			);
		} catch (err) {
			console.log(err);
		}
	};

	const fetchPhase2FolderNames = async (projectPath) => {
		if (!phase2 || !phase2.phase_type_id) return [];

		try {
			const folderListRes = await getAPICall(
				instance,
				accounts[0],
				"/api/dropbox/folders/byPhaseType",
				{
					phaseTypeID: phase2.phase_type_id,
				}
			);

			return folderListRes.data.map(
				(folder) =>
					`${projectPath}/${phase2.steve_number}. ${phase2.abbreviated_name}/${folder.folder_name}`
			);
		} catch (err) {
			console.log(err);
		}
	};

	const fetchPhase3FolderNames = async (projectPath) => {
		if (!phase3 || !phase3.phase_type_id) return [];

		try {
			const folderListRes = await getAPICall(
				instance,
				accounts[0],
				"/api/dropbox/folders/byPhaseType",
				{
					phaseTypeID: phase3.phase_type_id,
				}
			);

			return folderListRes.data.map(
				(folder) =>
					`${projectPath}/${phase3.steve_number}. ${phase3.abbreviated_name}/${folder.folder_name}`
			);
		} catch (err) {
			console.log(err);
		}
	};

	const fetchPhase4FolderNames = async (projectPath) => {
		if (!phase4 || !phase4.phase_type_id) return [];

		try {
			const folderListRes = await getAPICall(
				instance,
				accounts[0],
				"/api/dropbox/folders/byPhaseType",
				{
					phaseTypeID: phase4.phase_type_id,
				}
			);

			return folderListRes.data.map(
				(folder) =>
					`${projectPath}/${phase4.steve_number}. ${phase4.abbreviated_name}/${folder.folder_name}`
			);
		} catch (err) {
			console.log(err);
		}
	};

	// BQE
	const createBQERecords = async (
		propertyCode,
		projectCode,
		phase1Code,
		phase2Code,
		phase3Code,
		phase4Code
	) => {
		try {
			// BQE API requires a client to create a property, project, or phase
			const clientID = await determineBQEClientID();

			if (!clientID) {
				return false;
			}

			// Create property and store it's ID for project and phase creation
			const propertyID = await createBQEPropertyRecord(
				clientID,
				propertyCode
			);

			if (!propertyID) {
				openErrorAlert("Failed to create BQE property.");
				return false;
			}

			// Create project and store it's ID for phase creation
			const projectID = await createBQEProjectRecord(
				clientID,
				propertyID,
				projectCode
			);

			if (!projectID) {
				openErrorAlert("Failed to create BQE project.");
				return false;
			}

			// Create phase records
			const phaseRecordsCreated = await createBQEPhaseRecords(
				clientID,
				propertyID,
				projectID,
				phase1Code,
				phase2Code,
				phase3Code,
				phase4Code
			);

			if (!phaseRecordsCreated) {
				openErrorAlert("Failed to create BQE phases.");
				return false;
			}

			// Return all the IDs
			return {
				propertyID: propertyID,
				projectID: projectID,
				...phaseRecordsCreated,
			};
		} catch (err) {
			console.log(err);
		}
	};

	const searchBQEClient = async () => {
		// Check to see if there's already a client tied to the company name from the HS deal
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/client/byName",
				{
					clientName: pendingJobRecord.hs_company_name,
				}
			);

			return res.data.length > 0 ? res.data[0].id : null;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEClient = async () => {
		// Create a client record in BQE
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/client/create",
				{
					clientName: pendingJobRecord.hs_company_name,
					addressLine1: pendingJobRecord.hs_company_address_line1,
					addressLine2: pendingJobRecord.hs_company_address_line2,
					city: pendingJobRecord.hs_company_city,
					state: pendingJobRecord.hs_company_state,
					zip: pendingJobRecord.hs_company_zip,
				}
			);

			if (!res.data || !res.data.id) return null;

			return res.data.id;
		} catch (err) {
			console.log(err);
		}
	};

	const determineBQEClientID = async () => {
		// Search for BQE client, if it doesn't exist, create it and return the ID
		try {
			editProgressBar(42, "Searching for BQE client");
			const clientExistsID = await searchBQEClient();

			if (clientExistsID) {
				return clientExistsID;
			}

			editProgressBar(45, "Creating BQE client");
			const newClientID = await createBQEClient();

			if (!newClientID) {
				openErrorAlert("Failed to create BQE client.");
				return null;
			}

			return newClientID;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEPropertyRecord = async (clientID, propertyCode) => {
		// Create a new BQE property record (if it doesn't already exist) and return it's ID
		try {
			if (bqePropertyRecord && bqePropertyRecord.id)
				return bqePropertyRecord.id;

			// CREATE PROPERTY RECORD
			editProgressBar(55, "Creating BQE property record");
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/property/create",
				{
					clientId: clientID,
					propertyCode: propertyCode,
					managerId: dealOwner.bqe_id,
					propertyName: propertyInfo.name,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					oic: pendingJobRecord.oic,
					classId: pendingJobRecord.bqe_state_class_code,
				}
			);

			if (!res.data || !res.data.id) {
				return null;
			}

			// ASSOCIATE BUILDING TYPE PROJECT GROUP
			await associateProjectGroup(
				res.data.id,
				allBuildingTypes.find(
					(type) => type.id === propertyInfo.buildingTypeID
				)?.bqe_project_group_id,
				propertyInfo.displayName
			);

			// ASSOCIATE OIC
			await associateOICCustomField(res.data.id);

			return res.data.id;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEProjectRecord = async (
		clientID,
		propertyID,
		projectCode
	) => {
		// Create a new BQE project record (if it doesn't already exist) and return it's ID
		try {
			if (bqeProjectRecordID != null) return bqeProjectRecordID;

			// CREATE PROJECT RECORD
			editProgressBar(65, "Creating BQE project record");
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/create",
				{
					clientId: clientID,
					projectCode: projectCode,
					managerId: dealOwner.bqe_id,
					projectName: projectInfo.displayName,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					oic: pendingJobRecord.oic,
					classId: pendingJobRecord.bqe_state_class_code,
					parentId: propertyID,
					rootId: propertyID,
				}
			);

			if (!res.data || !res.data.id) {
				return null;
			}

			// ASSOCIATE PROJECT GROUP FOR COMPONENT 1
			await associateProjectGroup(
				res.data.id,
				selectedComponents[0].bqe_project_group_id,
				projectInfo.displayName
			);

			// ASSOCIATE PROJECT GROUP FOR COMPONENT 2 (if applicable)
			if (selectedComponents.length > 1) {
				await associateProjectGroup(
					res.data.id,
					selectedComponents[1].bqe_project_group_id,
					projectInfo.displayName
				);
			}

			// ASSOCIATE OIC
			await associateOICCustomField(res.data.id);

			return res.data.id;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEPhaseRecords = async (
		clientID,
		propertyID,
		projectID,
		phase1Code,
		phase2Code,
		phase3Code,
		phase4Code
	) => {
		// Create a new BQE phase record for each phase (if applicable) and return their IDs
		try {
			editProgressBar(70, "Creating BQE phase records");
			const phase1ID = await createBQEPhase1Record(
				clientID,
				propertyID,
				projectID,
				phase1Code
			);
			const phase2ID = await createBQEPhase2Record(
				clientID,
				propertyID,
				projectID,
				phase2Code
			);
			const phase3ID = await createBQEPhase3Record(
				clientID,
				propertyID,
				projectID,
				phase3Code
			);
			const phase4ID = await createBQEPhase4Record(
				clientID,
				propertyID,
				projectID,
				phase4Code
			);

			if (!phase1ID || !phase2ID || !phase3ID || !phase4ID) return false;

			return {
				phase1ID: phase1ID,
				phase2ID: phase2ID,
				phase3ID: phase3ID,
				phase4ID: phase4ID,
			};
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEPhase1Record = async (
		clientID,
		propertyID,
		projectID,
		phaseCode
	) => {
		// Create a new BQE phase record (if necessary), return it's ID, and associate an activity group. If phase 1 isn't requested, return 1.
		try {
			if (!phase1 || !phase1.id) return 1;

			editProgressBar(72, "Creating BQE phase 1 record");

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/create",
				{
					clientId: clientID,
					phaseCode: phaseCode,
					managerId: dealOwner.bqe_id,
					phaseName:
						phaseCostBases.phase1 === "flatFee"
							? `FF ${phase1.abbreviated_name}`
							: phase1.abbreviated_name,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					oic: pendingJobRecord.oic,
					classId: pendingJobRecord.bqe_state_class_code,
					parentId: projectID,
					rootId: propertyID,
					contractAmount: phaseBudgets.phase1,
					contractType: phaseCostBases.phase1 === "flatFee" ? 1 : 0,
				}
			);

			if (res.data && res.data.id) {
				await associateActivityGroup(
					res.data.id,
					phase1.activity_group_id
				);

				// Associate Phase Name Group ID
				await associateProjectGroup(
					res.data.id,
					phase1.bqe_project_group_id,
					phase1.abbreviated_name
				);

				// Associate Phase Type Group ID
				await associateProjectGroup(
					res.data.id,
					phase1.phase_type_bqe_project_group_id,
					phase1.abbreviated_name
				);

				// Associate Contract Type Group ID
				await associateProjectGroup(
					res.data.id,
					phaseCostBases.phase1 === "flatFee"
						? "569239c1-d549-4608-bf42-6351f71f0213"
						: "07ba1d09-5562-4792-a59d-35b94d50954d", // BQE group IDS for FF and T&C, respectively
					phase1.abbreviated_name
				);

				// ASSOCIATE OIC
				await associateOICCustomField(res.data.id);
			}

			return res.data && res.data.id ? res.data.id : null;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEPhase2Record = async (
		clientID,
		propertyID,
		projectID,
		phaseCode
	) => {
		// Create a new BQE phase record (if necessary), return it's ID, and associate an activity group. If phase 2 isn't requested, return 1.
		try {
			if (!phase2 || !phase2.id) return 1;

			editProgressBar(74, "Creating BQE phase 2 record");

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/create",
				{
					clientId: clientID,
					phaseCode: phaseCode,
					managerId: dealOwner.bqe_id,
					phaseName:
						phaseCostBases.phase2 === "flatFee"
							? `FF ${phase2.abbreviated_name}`
							: phase2.abbreviated_name,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					oic: pendingJobRecord.oic,
					classId: pendingJobRecord.bqe_state_class_code,
					parentId: projectID,
					rootId: propertyID,
					contractAmount: phaseBudgets.phase2,
					contractType: phaseCostBases.phase2 === "flatFee" ? 1 : 0,
				}
			);

			if (res.data && res.data.id) {
				await associateActivityGroup(
					res.data.id,
					phase2.activity_group_id
				);

				// Associate Phase Name Group ID
				await associateProjectGroup(
					res.data.id,
					phase2.bqe_project_group_id,
					phase2.abbreviated_name
				);

				// Associate Phase Type Group ID
				await associateProjectGroup(
					res.data.id,
					phase2.phase_type_bqe_project_group_id,
					phase2.abbreviated_name
				);

				// Associate Contract Type Group ID
				await associateProjectGroup(
					res.data.id,
					phaseCostBases.phase2 === "flatFee"
						? "569239c1-d549-4608-bf42-6351f71f0213"
						: "07ba1d09-5562-4792-a59d-35b94d50954d", // BQE group IDS for FF and T&C, respectively
					phase2.abbreviated_name
				);

				// ASSOCIATE OIC
				await associateOICCustomField(res.data.id);
			}

			return res.data && res.data.id ? res.data.id : null;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEPhase3Record = async (
		clientID,
		propertyID,
		projectID,
		phaseCode
	) => {
		// Create a new BQE phase record (if necessary), return it's ID, and associate an activity group. If phase 3 isn't requested
		try {
			if (!phase3 || !phase3.id) return 1;

			editProgressBar(76, "Creating BQE phase 3 record");

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/create",
				{
					clientId: clientID,
					phaseCode: phaseCode,
					managerId: dealOwner.bqe_id,
					phaseName:
						phaseCostBases.phase3 === "flatFee"
							? `FF ${phase3.abbreviated_name}`
							: phase3.abbreviated_name,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					oic: pendingJobRecord.oic,
					classId: pendingJobRecord.bqe_state_class_code,
					parentId: projectID,
					rootId: propertyID,
					contractAmount: phaseBudgets.phase3,
					contractType: phaseCostBases.phase3 === "flatFee" ? 1 : 0,
				}
			);

			if (res.data && res.data.id) {
				await associateActivityGroup(
					res.data.id,
					phase3.activity_group_id
				);

				// Associate Phase Name Group ID
				await associateProjectGroup(
					res.data.id,
					phase3.bqe_project_group_id,
					phase3.abbreviated_name
				);

				// Associate Phase Type Group ID
				await associateProjectGroup(
					res.data.id,
					phase3.phase_type_bqe_project_group_id,
					phase3.abbreviated_name
				);

				// Associate Contract Type Group ID
				await associateProjectGroup(
					res.data.id,
					phaseCostBases.phase3 === "flatFee"
						? "569239c1-d549-4608-bf42-6351f71f0213"
						: "07ba1d09-5562-4792-a59d-35b94d50954d", // BQE group IDS for FF and T&C, respectively
					phase3.abbreviated_name
				);

				// ASSOCIATE OIC
				await associateOICCustomField(res.data.id);
			}

			return res.data && res.data.id ? res.data.id : null;
		} catch (err) {
			console.log(err);
		}
	};

	const createBQEPhase4Record = async (
		clientID,
		propertyID,
		projectID,
		phaseCode
	) => {
		// Create a new BQE phase record (if necessary), return it's ID, and associate an activity group. If phase 4 isn't requested
		try {
			if (!phase4 || !phase4.id) return 1;

			editProgressBar(78, "Creating BQE phase 4 record");

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/create",
				{
					clientId: clientID,
					phaseCode: phaseCode,
					managerId: dealOwner.bqe_id,
					phaseName:
						phaseCostBases.phase4 === "flatFee"
							? `FF ${phase4.abbreviated_name}`
							: phase4.abbreviated_name,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					oic: pendingJobRecord.oic,
					classId: pendingJobRecord.bqe_state_class_code,
					parentId: projectID,
					rootId: propertyID,
					contractAmount: phaseBudgets.phase4,
					contractType: phaseCostBases.phase4 === "flatFee" ? 1 : 0,
				}
			);

			if (res.data && res.data.id) {
				await associateActivityGroup(
					res.data.id,
					phase4.activity_group_id
				);

				// Associate Phase Name Group ID
				await associateProjectGroup(
					res.data.id,
					phase4.bqe_project_group_id,
					phase4.abbreviated_name
				);

				// Associate Phase Type Group ID
				await associateProjectGroup(
					res.data.id,
					phase4.phase_type_bqe_project_group_id,
					phase4.abbreviated_name
				);

				// Associate Contract Type Group ID
				await associateProjectGroup(
					res.data.id,
					phaseCostBases.phase4 === "flatFee"
						? "569239c1-d549-4608-bf42-6351f71f0213"
						: "07ba1d09-5562-4792-a59d-35b94d50954d", // BQE group IDS for FF and T&C, respectively
					phase4.abbreviated_name
				);

				// ASSOCIATE OIC
				await associateOICCustomField(res.data.id);
			}

			return res.data && res.data.id ? res.data.id : null;
		} catch (err) {
			console.log(err);
		}
	};

	const associateActivityGroup = async (phaseID, groupID) => {
		// Associate an activity group with a phase
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/assignActivityGroup",
				{
					phaseID: phaseID,
					activityGroupID: groupID,
				}
			);

			return res;
		} catch (err) {
			console.log(err);
		}
	};

	const associateProjectGroup = async (projectID, groupID, entityName) => {
		try {
			const assocRes = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/group/assign",
				{
					entityId: projectID,
					groupId: groupID,
					projectName: entityName,
				}
			);

			return assocRes.data;
		} catch (err) {
			console.log(err);
		}
	};

	const associateOICCustomField = async (projectID) => {
		try {
			var bqeOICValue = null; // This is the ID of the dropdown list value for the OIC in our system

			if (projectInfo.oic === "WA") {
				bqeOICValue = "d5e226fe-5798-43a4-a752-7bcd087eeda3"; // WA OIC dropdown ID
			} else if (projectInfo.oic === "OR") {
				bqeOICValue = "03356b2a-8467-46ef-9ce7-cf4dc99599d3"; // OR OIC dropdown ID
			} else if (projectInfo.oic === "UT") {
				bqeOICValue = "60d08179-1541-41ee-938f-9415ba4b5872"; // UT OIC dropdown ID
			} else {
				bqeOICValue = "d5e226fe-5798-43a4-a752-7bcd087eeda3"; // Default to WA OIC dropdown ID
			}

			const assocRes = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/oic/assign",
				{
					bqeProjectId: projectID,
					bqeOICValue: bqeOICValue,
				}
			);

			return assocRes.data;
		} catch (err) {
			console.log(err);
		}
	};

	// Company Cam
	const createCompanyCamProjects = async () => {
		// Create a Company Cam project for each phase and return their IDs

		editProgressBar(82, "Creating Phase 1 Company Cam Project");
		const phase1Res = await createCompanyCamProject(phase1);
		if (!phase1Res || phase1Res.error) {
			openErrorAlert("Failed to create Company Cam project for Phase 1.");
			return {
				error: {
					step: "createCompanyCamProject",
					phase: "phase1",
					detail:
						phase1Res?.error ??
						"Unknown error creating Company Cam project for Phase 1.",
				},
			};
		}

		editProgressBar(84, "Creating Phase 2 Company Cam Project");
		const phase2Res = await createCompanyCamProject(phase2);
		if (!phase2Res || phase2Res.error) {
			openErrorAlert("Failed to create Company Cam project for Phase 2.");
			return {
				error: {
					step: "createCompanyCamProject",
					phase: "phase2",
					detail:
						phase2Res?.error ??
						"Unknown error creating Company Cam project for Phase 2.",
				},
			};
		}

		editProgressBar(86, "Creating Phase 3 Company Cam Project");
		const phase3Res = await createCompanyCamProject(phase3);
		if (!phase3Res || phase3Res.error) {
			openErrorAlert("Failed to create Company Cam project for Phase 3.");
			return {
				error: {
					step: "createCompanyCamProject",
					phase: "phase3",
					detail:
						phase3Res?.error ??
						"Unknown error creating Company Cam project for Phase 3.",
				},
			};
		}

		editProgressBar(88, "Creating Phase 4 Company Cam Project");
		const phase4Res = await createCompanyCamProject(phase4);
		if (!phase4Res || phase4Res.error) {
			openErrorAlert("Failed to create Company Cam project for Phase 4.");
			return {
				error: {
					step: "createCompanyCamProject",
					phase: "phase4",
					detail:
						phase4Res?.error ??
						"Unknown error creating Company Cam project for Phase 4.",
				},
			};
		}

		return {
			phase1ID: phase1Res !== 1 ? phase1Res.data.company_cam_id : null,
			phase2ID: phase2Res !== 1 ? phase2Res.data.company_cam_id : null,
			phase3ID: phase3Res !== 1 ? phase3Res.data.company_cam_id : null,
			phase4ID: phase4Res !== 1 ? phase4Res.data.company_cam_id : null,
		};
	};

	const arrayBufferToBase64 = (buffer) => {
		let binary = "";
		const bytes = new Uint8Array(buffer);
		const len = bytes.byteLength;
		for (let i = 0; i < len; i++) {
			binary += String.fromCharCode(bytes[i]);
		}
		return btoa(binary);
	};

	const getMimeTypeFromName = (name) => {
		if (!name) return "application/octet-stream";

		const lower = name.toLowerCase();

		if (lower.endsWith(".pdf")) return "application/pdf";
		if (lower.endsWith(".docx"))
			return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
		if (lower.endsWith(".doc")) return "application/msword";
		if (lower.endsWith(".png")) return "image/png";
		if (lower.endsWith(".jpg") || lower.endsWith(".jpeg"))
			return "image/jpeg";

		return "application/octet-stream";
	};

	const downloadDropboxFileAsBase64 = async (path) => {
		try {
			const tokenRes = await instance.acquireTokenSilent({
				...loginRequest,
				account: accounts[0],
			});

			const res = await axios.post(
				"/api/dropbox/download",
				{ path },
				{
					headers: {
						Authorization: `Bearer ${tokenRes.accessToken}`,
						"Content-Type": "application/json",
					},
					responseType: "arraybuffer",
				}
			);

			return arrayBufferToBase64(res.data);
		} catch (err) {
			console.log(`Failed to download Dropbox file (${path})`, err);
			return null;
		}
	};

	const createCompanyCamProject = async (phase) => {
		if (!phase || !phase.id) return 1;

		const coordinates = {
			lat:
				pendingJobRecord.property_latitude ??
				pendingJobRecord.property_lat ??
				pendingJobRecord.latitude ??
				null,
			lon:
				pendingJobRecord.property_longitude ??
				pendingJobRecord.property_lon ??
				pendingJobRecord.longitude ??
				null,
		};
		const hasCoordinates =
			coordinates.lat !== null && coordinates.lon !== null;

		const primaryContact =
			pendingJobRecord.contact_name || pendingJobRecord.contact_email
				? {
						name: pendingJobRecord.contact_name || undefined,
						email: pendingJobRecord.contact_email || undefined,
				  }
				: null;

		const companyCamUserEmail = pendingJobRecord.contact_email;

		const projectName = `${propertyInfo.name}: ${projectInfo.displayName}: ${phase.abbreviated_name}`;

		const payload = {
			name: projectName,
			address: {
				street_address_1: propertyInfo.addressLine1,
				street_address_2: propertyInfo.addressLine2,
				city: propertyInfo.city,
				state: propertyInfo.state,
				postal_code: propertyInfo.zip,
			},
		};

		if (hasCoordinates) {
			payload.coordinates = coordinates;
		}

		if (primaryContact) {
			payload.primary_contact = primaryContact;
		}

		let projectRes;
		try {
			projectRes = await postAPICall(
				instance,
				accounts[0],
				"/api/companyCam/projects",
				payload,
				employeeID
			);
		} catch (err) {
			console.log("Error creating CompanyCam project: ", err);
			return { error: err };
		}

		const projectId = projectRes?.data?.id;
		if (!projectId) {
			return {
				error: "CompanyCam project creation did not return an id",
			};
		}

		try {
			if (dealOwner.company_cam_id) {
				await putAPICall(
					instance,
					accounts[0],
					`/api/companyCam/projects/${projectId}/assigned_users/${dealOwner.company_cam_id}`,
					{
						userEmail: companyCamUserEmail,
					},
					employeeID
				);
			}
		} catch (err) {
			console.log("Error assigning CompanyCam user to project: ", err);
			return { error: err };
		}

		try {
			await postAPICall(
				instance,
				accounts[0],
				`/api/companyCam/projects/${projectId}/checklists`,
				{
					checklist_template_id: "63801",
					userEmail: companyCamUserEmail,
				},
				employeeID
			);

			await postAPICall(
				instance,
				accounts[0],
				`/api/companyCam/projects/${projectId}/checklists`,
				{
					checklist_template_id: "83392",
					userEmail: companyCamUserEmail,
				},
				employeeID
			);
		} catch (err) {
			console.log("Error adding CompanyCam checklists: ", err);
			return { error: err };
		}

		const labels = [
			projectInfo.oic,
			"Active",
			phase.phase_type || phase.name || phase.abbreviated_name,
		];

		if (
			phase.phase_type &&
			phase.phase_type.toLowerCase() === "diagnose" &&
			phase.name
		) {
			labels.push(phase.name);
		}

		for (const label of labels.filter(Boolean)) {
			try {
				await postAPICall(
					instance,
					accounts[0],
					`/api/companyCam/projects/${projectId}/labels`,
					{
						project: {
							labels: [label],
						},
						userEmail: companyCamUserEmail,
					},
					employeeID
				);
			} catch (err) {
				console.log(`Error adding CompanyCam label ${label}: `, err);
				return { error: err };
			}
		}

		if (
			(phase.abbreviated_name &&
				phase.abbreviated_name.toUpperCase() === "PCA") ||
			(phase.name && phase.name.toUpperCase() === "PCA")
		) {
			const pcaWarnings = [];
			const pcaDocs = [
				{
					name: "ASTM-E-2018-24.pdf",
					path: "/SW_Engineering/Pending Jobs/PCA/ASTM-E-2018-24.pdf",
				},
				{
					name: "Property Condition Assessment PCA Internal document.docx",
					path: "/SW_Engineering/Pending Jobs/PCA/Property Condition Assessment PCA Internal document.docx",
				},
			];

			for (const doc of pcaDocs) {
				const attachmentBase64 = await downloadDropboxFileAsBase64(
					doc.path
				);

				if (!attachmentBase64) {
					const msg = `Failed to download PCA document ${doc.name} from ${doc.path}`;
					console.log(msg);
					pcaWarnings.push(msg);
					continue;
				}

				try {
					await postAPICall(
						instance,
						accounts[0],
						`/api/companyCam/projects/${projectId}/documents`,
						{
							document: {
								name: doc.name,
								attachment: `data:${getMimeTypeFromName(
									doc.name
								)};base64,${attachmentBase64}`,
							},
							userEmail: companyCamUserEmail,
						},
						employeeID
					);
				} catch (err) {
					console.log(
						`Error uploading CompanyCam document ${doc.name}: `,
						err
					);
					pcaWarnings.push(
						`Failed to upload PCA document ${doc.name} to CompanyCam`
					);
					continue;
				}
			}
		}

		return { data: { company_cam_id: projectId } };
	};

	// MySQL
	const createPropertyRecord = async (bqeID, bqeCode, filePath) => {
		// Create a new property record in the database (if it doesn't already exist) and return it's ID
		try {
			// LOOK UP THE HUBSPOT PROPERTY OBJECT ASSOCIATED TO THIS DEAL
			let propertyHubspotID = null;
			if (pendingJobRecord.hs_deal_id) {
				try {
					const hubspotPropertyRes = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/getPropertyFromDeal",
						{ dealID: pendingJobRecord.hs_deal_id }
					);
					propertyHubspotID =
						hubspotPropertyRes.data?.propertyID || null;
				} catch (err) {
					console.log(err);
				}
			}

			// SEARCH EXISTING
			const searchRes = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/byName",
				{
					name: propertyInfo.name,
				}
			);

			if (searchRes.data && searchRes.data.length > 0) {
				const existingProperty = searchRes.data[0];

				// BACKFILL A MISSING HUBSPOT ID ON THE EXISTING RECORD
				if (!existingProperty.hubspot_id && propertyHubspotID) {
					try {
						await putAPICall(
							instance,
							accounts[0],
							"/api/jobs/property/update",
							{
								id: existingProperty.id,
								name: existingProperty.name,
								addressLine1: existingProperty.address_line1,
								addressLine2: existingProperty.address_line2,
								city: existingProperty.city,
								state: existingProperty.state,
								zip: existingProperty.zip,
								dropboxFilePath:
									existingProperty.dropbox_file_path,
								bqeID: existingProperty.bqe_id,
								bqeCode: existingProperty.bqe_code,
								oic: existingProperty.oic,
								hubspotID: propertyHubspotID,
							},
							employeeID
						);
					} catch (err) {
						console.log(err);
					}
				}

				return existingProperty.id;
			}

			// CREATE
			const creationRes = await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/create",
				{
					name: propertyInfo.name,
					addressLine1: propertyInfo.addressLine1,
					addressLine2: propertyInfo.addressLine2,
					city: propertyInfo.city,
					state: propertyInfo.state,
					zip: propertyInfo.zip,
					dropboxFilePath: filePath,
					bqeID: bqeID,
					bqeCode: bqeCode,
					oic: projectInfo.oic,
					hubspotID: propertyHubspotID,
				},
				employeeID
			);

			return creationRes.data && creationRes.data.insertId
				? creationRes.data.insertId
				: null;
		} catch (err) {
			console.log(err);
		}
	};

	const createProjectRecord = async (
		bqeID,
		bqeCode,
		filePath,
		propertyID
	) => {
		// Create a new project record in the database (if it doesn't already exist) and return it's ID
		try {
			// SEARCH EXISTING
			const searchRes = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/byName",
				{
					name: projectInfo.name,
					propertyID: propertyID,
				}
			);

			if (searchRes.data && searchRes.data.length > 0) {
				return searchRes.data[0].id;
			}

			// CREATE
			const creationRes = await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/create",
				{
					name: projectInfo.name,
					displayName: projectInfo.displayName,
					dropboxPath: filePath,
					bqeID: bqeID,
					bqeCode: bqeCode,
					hubspotID: pendingJobRecord.hs_deal_id,
					quoteID: pendingJobRecord.hs_quote_id,
					propertyID: propertyID,
					ownerID: dealOwner.id,
				},
				employeeID
			);

			if (!creationRes.data || !creationRes.data.insertId) {
				return null;
			}

			// ASSOCIATE DEAL TO PROJECT
			const associationRes = await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/associate/to/deal",
				{
					hubspotID: pendingJobRecord.hs_deal_id,
					projectID: creationRes.data.insertId,
				}
			);

			return creationRes.data.insertId;
		} catch (err) {
			console.log(err);
		}
	};

	const createPhaseRecord = async (
		phase,
		bqeID,
		bqeCode,
		filePath,
		companyCamID,
		propertyID,
		projectID
	) => {
		// Create a new phase record in the database (if it doesn't already exist) and return it's ID
		if (!phase || !phase.id) return null;

		try {
			// SEARCH EXISTING
			const searchRes = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phase/byName",
				{
					name: phase.name,
					projectID: projectID,
				}
			);

			if (searchRes.data && searchRes.data.length > 0) {
				return searchRes.data[0].id;
			}

			// CREATE
			const creationRes = await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/phase/create",
				{
					name: phase.name,
					dropboxPath: filePath,
					bqeID: bqeID,
					bqeCode: bqeCode,
					hubspotID: pendingJobRecord.hs_deal_id,
					companyCamID: companyCamID,
					propertyID: propertyID,
					projectID: projectID,
					ownerID: dealOwner.id,
					phaseTypeID: phase.phase_type_id,
				},
				employeeID
			);

			if (!creationRes.data || creationRes.data.error) {
				openErrorAlert(
					`Failed to create phase record for ${phase.name}.`
				);
				return null;
			}

			return creationRes.data.insertId;
		} catch (err) {
			console.log(err);
		}
	};

	// Various
	const notifyDealOwner = async (
		propertyID,
		projectID,
		phase1ID,
		phase2ID,
		phase3ID,
		phase4ID
	) => {
		// Send a webhook to a Make Scenario which notifies the deal owner
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/outlook/pendingJob/emailDealOwner",
				{
					propertyID: propertyID,
					projectID: projectID,
					phase1ID: phase1ID,
					phase2ID: phase2ID,
					phase3ID: phase3ID,
					phase4ID: phase4ID,
					dealOwnerID: dealOwner.id,
				}
			);

			return res;
		} catch (err) {
			console.log(err);
		}
	};

	const pendingJobSubmitted = async () => {
		// Update the pending job record to submitted
		try {
			const res = await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/pending/submitted",
				{
					submittedByID: employeeID,
					id: pendingJobRecord.id,
				},
				employeeID
			);

			return res;
		} catch (err) {
			console.log(err);
		}
	};

	const notifyProjectCoordinator = async (
		propertyID,
		projectID,
		phase1ID,
		phase2ID,
		phase3ID,
		phase4ID
	) => {
		// This function notifies the project coordinator to manually complete any tasks that the automation failed on
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/outlook/pendingJob/emailProjectCoordinator",
				{
					propertyID: propertyID,
					projectID: projectID,
					phase1ID: phase1ID,
					phase2ID: phase2ID,
					phase3ID: phase3ID,
					phase4ID: phase4ID,
					dealOwnerID: dealOwner.id,
					submittedBy: employeeID,
				}
			);

			return res;
		} catch (err) {
			console.log(err);
		}
	};

	// HELPER FUNCTIONS
	// Validate Information
	const validateGeneralInformation = () => {
		// Property Information
		if (
			!propertyInfo.name ||
			!propertyInfo.addressLine1 ||
			!propertyInfo.city ||
			!propertyInfo.state ||
			!propertyInfo.zip.match(zipCodeRegex) ||
			!propertyInfo.buildingTypeID
		) {
			openErrorAlert(
				"Please ensure all property information is filled out correctly."
			);
			return false;
		}

		// Project Information
		if (
			!projectInfo.name ||
			!projectInfo.displayName ||
			!projectInfo.oic ||
			!selectedComponents.length
		) {
			openErrorAlert(
				"Please ensure all project information is filled out correctly."
			);
			return false;
		}

		// Phase Information
		if (!phase1 || !phaseBudgets.phase1 || !phaseCostBases.phase1) {
			openErrorAlert(
				"Please ensure all phase information for Phase 1 is filled out correctly."
			);
			return false;
		}

		// Checks to see if phase 2 info needs to be filled out
		if (
			numberOfPhases >= 2 &&
			(!phase2 || !phaseBudgets.phase2 || !phaseCostBases.phase2)
		) {
			openErrorAlert(
				"Please ensure all phase information for Phase 2 is filled out correctly."
			);
			return false;
		}

		// Checks to see if phase 3 info needs to be filled out
		if (
			numberOfPhases >= 3 &&
			(!phase3 || !phaseBudgets.phase3 || !phaseCostBases.phase3)
		) {
			openErrorAlert(
				"Please ensure all phase information for Phase 3 is filled out correctly."
			);
			return false;
		}

		// Checks to see if phase 4 info needs to be filled out
		if (
			numberOfPhases >= 4 &&
			(!phase4 || !phaseBudgets.phase4 || !phaseCostBases.phase4)
		) {
			openErrorAlert(
				"Please ensure all phase information for Phase 4 is filled out correctly."
			);
			return false;
		}

		// Deal Owner Information
		if (!dealOwner.id) {
			openErrorAlert("Please ensure the deal owner is selected.");
			return false;
		}

		if (!dealOwner.company_cam_id) {
			openErrorAlert(
				"Employees must be set up in Company Cam to be Deal Owner. If you believe this is an error please contact IT."
			);
			return false;
		}

		return true;
	};

	const validateDropboxInformation = () => {
		if (!isDropboxInformationValid) {
			openErrorAlert(
				"Please ensure the Dropbox information is filled out correctly."
			);
			return false;
		}

		return true;
	};

	const validateBQEInformation = () => {
		if (!isBqeInformationValid) {
			openErrorAlert(
				"Please ensure the BQE information is filled out correctly."
			);
			return false;
		}

		return true;
	};

	// Feedback methods
	const openErrorAlert = (message) => {
		setErrorAlertMessage(message);
		setErrorAlertOpen(true);

		// scroll to top
		topScreenRef.current.scrollIntoView({ behavior: "smooth" });

		setProgressBarLevel(0);
		setProgressBarMessage("");
		setProgressBarOpen(false);
	};

	const closeErrorAlert = () => {
		setErrorAlertMessage("");
		setErrorAlertOpen(false);
	};

	const openSuccessAlert = (message) => {
		setSuccessAlertMessage(message);
		setSuccessAlertOpen(true);

		// scroll to top
		topScreenRef.current.scrollIntoView({ behavior: "smooth" });

		setProgressBarLevel(0);
		setProgressBarMessage("");
		setProgressBarOpen(false);
	};

	const closeSuccessAlert = () => {
		setSuccessAlertMessage("");
		setSuccessAlertOpen(false);
	};

	const editProgressBar = (level, message) => {
		setProgressBarLevel(level);
		setProgressBarMessage(message);
	};

	// RNG methods
	const generatePropertyCode = async () => {
		// This generates a unique property code (for BQE), if it fails to generate a unique code after 10 tries, it will return null
		// Code format: PP-XXXXX (5 uppercase pseudorandom alphanumeric characters)
		try {
			var code = generateAlphanumericCode();
			var propertyCode = `PP-${code}`;

			var count = 0;
			while (count < 10) {
				// Try 10 times to generate a unique property code
				const res = await getAPICall(
					instance,
					accounts[0],
					"/api/jobs/property/byBQECode",
					{
						bqeCode: propertyCode,
					}
				);

				if (res.data.length === 0) {
					return propertyCode;
				}

				code = generateAlphanumericCode();
				propertyCode = `PP-${code}`;
				count++;
			}

			openErrorAlert("Failed to generate a unique property code.");
			return null;
		} catch (err) {
			openErrorAlert("Failed to generate a unique property code.");
			console.log(err);
		}
	};

	const generateProjectCode = async () => {
		// This generates a unique project code (for BQE), if it fails to generate a unique code after 10 tries, it will return null
		// Code format: PJ-XXXXX (5 uppercase pseudorandom alphanumeric characters)
		try {
			var code = generateAlphanumericCode();
			var projectCode = `PJ-${code}`;

			var count = 0;
			while (count < 10) {
				// Try 10 times to generate a unique project code
				const res = await getAPICall(
					instance,
					accounts[0],
					"/api/jobs/project/byBQECode",
					{
						bqeCode: projectCode,
					}
				);

				if (res.data.length === 0) {
					return projectCode;
				}

				code = generateAlphanumericCode();
				projectCode = `PJ-${code}`;
				count++;
			}

			openErrorAlert("Failed to generate a unique project code.");
			return null;
		} catch (err) {
			openErrorAlert("Failed to generate a unique project code.");
			console.log(err);
		}
	};

	const generatePhaseCode = async () => {
		// This generates a unique phase code (for BQE), if it fails to generate a unique code after 10 tries, it will return null
		// Code format: PH-XXXXX (5 uppercase pseudorandom alphanumeric characters)
		try {
			var code = generateAlphanumericCode();
			var phaseCode = `PH-${code}`;

			var count = 0;
			while (count < 10) {
				// Try 10 times to generate a unique phase code
				const res = await getAPICall(
					instance,
					accounts[0],
					"/api/jobs/phase/byBQECode",
					{
						bqeCode: phaseCode,
					}
				);

				if (res.data.length === 0) {
					return phaseCode;
				}

				code = generateAlphanumericCode();
				phaseCode = `PH-${code}`;
				count++;
			}

			openErrorAlert("Failed to generate a unique phase code.");
			return null;
		} catch (err) {
			openErrorAlert("Failed to generate a unique phase code.");
			console.log(err);
		}
	};

	// Helper to add Videos folders beneath any Photos folder.
	const withVideosUnderPhotos = (paths) => {
		const results = [...paths];

		paths.forEach((p) => {
			// Match ".../<code> Photos" at the end of a path, capturing the <code> (e.g., "1.0", "4.2")
			const m = p.match(/\/(\d+\.\d+)\s+Photos$/);
			if (!m) return;

			const code = m[1];
			// Always use .1 suffix for Videos
			const videosCode = `${code}.1`;

			// Create Videos folder path under the Photos folder
			const videosPath = `${p}/${videosCode} Videos`;
			results.push(videosPath);
		});

		return results;
	};

	// RENDER (ALREADY SUBMITTED)
	if (!isLoading && pendingJobRecord.is_submitted) {
		return (
			<div className="PendingJobReviewScreen" ref={topScreenRef}>
				<div className="pendingJobReviewScreenHeader">
					{pendingJobRecord.property_name}
				</div>
				<div className="pendingJobAlreadySubmittedMessage">
					This job has already been submitted.
				</div>
			</div>
		);
	}

	// RENDER (NOT SUBMITTED)
	return (
		<div className="PendingJobReviewScreen" ref={topScreenRef}>
			<div className="pendingJobReviewScreenHeader">
				{pendingJobRecord.property_name}
			</div>
			{isLoading && (
				<div className="pendingJobLoadingSpinner">
					<CircularProgress />
				</div>
			)}
			{!isLoading && (
				<>
					<Collapse
						sx={{ width: "stretch" }}
						id="pendingJobAlertCollapse"
						in={successAlertOpen}
					>
						<div
							className="pendingJobAlertCollapseContainer"
							ref={successMessageRef}
						>
							<Alert
								sx={{ width: "stretch" }}
								id="pendingJobSuccessAlert"
								severity="success"
								action={
									<IconButton
										id="pendingJobSuccessAlertCloseIconButton"
										aria-label="close"
										color="inherit"
										size="small"
										onClick={closeSuccessAlert}
									>
										<CloseIcon
											id="pendingJobSuccessAlertCloseIcon"
											fontSize="inherit"
										/>
									</IconButton>
								}
							>
								{successAlertMessage}
							</Alert>
						</div>
					</Collapse>
					<Collapse
						sx={{ width: "stretch" }}
						id="pendingJobAlertCollapse"
						in={errorAlertOpen}
					>
						<div className="pendingJobAlertCollapseContainer">
							<Alert
								sx={{ width: "stretch" }}
								id="pendingJobErrorAlert"
								severity="error"
								action={
									<IconButton
										id="pendingJobErrorAlertCloseIconButton"
										aria-label="close"
										color="inherit"
										size="small"
										onClick={closeErrorAlert}
									>
										<CloseIcon
											id="pendingJobErrorAlertCloseIcon"
											fontSize="inherit"
										/>
									</IconButton>
								}
							>
								{errorAlertMessage}
							</Alert>
						</div>
					</Collapse>
					<div className="jobCardContainer">
						<GeneralInformationCard
							allComponents={allComponents}
							allPhases={allPhases}
							activeEmployees={activeEmployees}
							propertyInfo={propertyInfo}
							setPropertyInfo={setPropertyInfo}
							projectInfo={projectInfo}
							setProjectInfo={setProjectInfo}
							dealOwner={dealOwner}
							setDealOwner={setDealOwner}
							selectedComponents={selectedComponents}
							setSelectedComponents={setSelectedComponents}
							numberOfPhases={numberOfPhases}
							setNumberOfPhases={setNumberOfPhases}
							phase1={phase1}
							phase2={phase2}
							phase3={phase3}
							phase4={phase4}
							setPhase1={setPhase1}
							setPhase2={setPhase2}
							setPhase3={setPhase3}
							setPhase4={setPhase4}
							phaseCostBases={phaseCostBases}
							setPhaseCostBases={setPhaseCostBases}
							phaseBudgets={phaseBudgets}
							setPhaseBudgets={setPhaseBudgets}
							buildingTypes={allBuildingTypes}
						/>
						<DropboxInformationCard
							filePath={filePath}
							setFilePath={setFilePath}
							propertyInfo={propertyInfo}
							projectInfo={projectInfo}
							folderCreationLevel={folderCreationLevel}
							setFolderCreationLevel={setFolderCreationLevel}
							setIsDropboxInformationValid={
								setIsDropboxInformationValid
							}
						/>
						<BQEInformationCard
							propertyInfo={propertyInfo}
							projectInfo={projectInfo}
							bqePropertyRecord={bqePropertyRecord}
							setBqePropertyRecord={setBqePropertyRecord}
							bqeProjectRecordID={bqeProjectRecordID}
							setBqeProjectRecordID={setBqeProjectRecordID}
							dealOwner={
								dealOwner &&
								dealOwner.first_name &&
								dealOwner.last_name
									? `${dealOwner.first_name} ${dealOwner.last_name}`
									: ""
							}
							isBqeInformationValid={isBqeInformationValid}
							setIsBqeInformationValid={setIsBqeInformationValid}
							bqePropertyRecordCreateNew={
								bqePropertyRecordCreateNew
							}
							setBqePropertyRecordCreateNew={
								setBqePropertyRecordCreateNew
							}
							bqeProjectRecordCreateNew={
								bqeProjectRecordCreateNew
							}
							setBqeProjectRecordCreateNew={
								setBqeProjectRecordCreateNew
							}
						/>
						<CompanyCamInformationCard
							propertyName={propertyInfo.name}
							projectDisplayName={projectInfo.displayName}
							phase1={phase1.abbreviated_name ?? ""}
							phase2={phase2.abbreviated_name ?? ""}
							phase3={phase3.abbreviated_name ?? ""}
							phase4={phase4.abbreviated_name ?? ""}
						/>
					</div>
					<Collapse
						sx={{ width: "stretch" }}
						id="progressBarCollapse"
						in={progressBarOpen}
					>
						<div className="progressBarCollapseContainer">
							<h2>{progressBarMessage}</h2>
							<LinearProgress
								sx={{ height: 10, borderRadius: 5 }}
								id="pendingJobProgressBar"
								variant="determinate"
								value={progressBarLevel}
							/>
						</div>
					</Collapse>
					<Collapse
						sx={{ width: "stretch" }}
						id="pendingJobAlertCollapse"
						in={isUnsavedChanges}
					>
						<div className="pendingJobAlertCollapseContainer">
							<Alert
								sx={{ width: "stretch" }}
								id="pendingJobWarningAlert"
								severity="warning"
							>
								You have unsaved changes. Please save before
								submitting.
							</Alert>
						</div>
					</Collapse>
					<div className="pendingJobsButtons">
						<Button
							variant="outlined"
							color="primary"
							onClick={saveButtonPressed}
						>
							Save
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={submitButtonPressed}
							disabled={isUnsavedChanges || isSubmitted}
						>
							Submit
						</Button>
					</div>
				</>
			)}
		</div>
	);
};

export default PendingJobReviewScreen;
